import { generatePath, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ValuesType } from 'utility-types';

// Import utilities
import { useProtectedPage, useGeoblock } from 'components/utilities';

// Import helpers
import { getButtonNameTranslation } from '../helpers';
import { getTypeTranslation } from 'helpers';
import { convertPriceToReadableFormat, TRANSACTION_TYPES } from 'helpers/prices';
import { VOD_PATHS } from 'components/routes/vod_routes/paths';

const { RENT, SUBSCRIPTION } = TRANSACTION_TYPES;

type Props = {
	uuid: string;
	transactionType: ValuesType<typeof TRANSACTION_TYPES>;
	price: number;
	period: number;
	title: string;
	cover: string | undefined;
	promotion: { id: number; price: number };
};

const usePriceButton = ({ uuid, transactionType, promotion, price, period, title, cover }: Props) => {
	const { t } = useTranslation();
	const { type } = useParams<{ type: string }>();
	const setProtectedPage = useProtectedPage((state) => state.setProtectedPage);

	const { checkUserLocation, isLoading } = useGeoblock({ context: 'purchase' });

	const buttonNameTranslation = getButtonNameTranslation({
		transactionType
	});

	const isSubscription = transactionType === SUBSCRIPTION;
	const hasPeriod = Boolean(transactionType === RENT && period);

	const isPromotion = Boolean(promotion && promotion.price !== null);

	const formattedPrice = convertPriceToReadableFormat(price);
	const formattedPromotion = isPromotion ? convertPriceToReadableFormat(promotion.price) : '';

	const transactionPath = generatePath(VOD_PATHS.TRANSACTION, {
		id: uuid,
		transactionType: getTypeTranslation(transactionType),
		type
	});

	const handleSetProtectedPath = () =>
		setProtectedPage({ pathname: transactionPath, title: t(`login:${transactionType}`, { title, period }), cover });

	return {
		buttonNameTranslation,
		formattedPromotion,
		formattedPrice,
		period: hasPeriod ? period : null,
		isSubscription,
		isLoading,
		transactionPath,
		checkUserLocation,
		handleSetProtectedPath
	};
};

export default usePriceButton;
